import _ from 'lodash';
import { getDynamicAppConfigs } from 'habitual-analytics/constants/dynamicAppConfigs';
import RestEndPoints from './RestEndPoints';
import {
  orderTableHeaders,
  tradeTableHeaders,
  postitionTableHeaders,
  holdingTableHeaders,
} from './tableHeaders';
import {
  parseHoldingsBook,
  parseMarginCalculator,
  parseModifyOrder,
  parseOrderDetails,
  parsePlaceOrder,
} from './parser';
import { wrappedFetch } from 'habitual-analytics/api/services/base';
import CryptoJS from 'crypto-js';
import { InstrumentDataAdaptor } from '../../api/services/adaptor';

export const REQUEST_PHASE_URL = () => {
  // todo for mobile app
  return '';
};

const requestHeaders = {
  'Content-Type': 'application/x-www-form-urlencoded',
};

const mandatoryRequestData = (brokerClientId) =>
  JSON.stringify({ uid: brokerClientId, actid: brokerClientId });

const fetchAccessToken = () => {
  // todo for mobile app
  return {};
};

const hashPassword = (password) => {
  // Sha256 hash 3 times
  const hashIterations = 3;
  let hash = password;

  for (let i = 0; i < hashIterations; i++) {
    hash = CryptoJS.SHA256(hash).toString(CryptoJS.enc.Hex);
  }

  return hash;
};

const sendOtp = (brokerClientId, password) => {
  const logindData = {
    uid: brokerClientId,
    pan: hashPassword(password),
  };
  const requestBody = `jData=${JSON.stringify(logindData)}&jKey=`;

  const parameters = {
    method: 'POST',
    headers: requestHeaders,
    body: requestBody,
  };

  const uri = RestEndPoints.SEND_OTP;
  return wrappedFetch(uri, parameters)
    .then((response) => response.ok && response.json())
    .then((res) => {
      return {
        status: res?.ReqStatus === 'OTP generation success',
        message: res?.ReqStatus,
      };
    })
    .catch(() => {
      return false;
    });
};

const isValidAccessToken = () => {
  const { brokerClientId, brokerClientAccessToken } =
    getDynamicAppConfigs().brokerConfigs;
  const requestBody = `jData=${mandatoryRequestData(
    brokerClientId
  )}&jKey=${brokerClientAccessToken}`;

  const parameters = {
    method: 'POST',
    headers: requestHeaders,
    body: requestBody,
  };

  const uri = RestEndPoints.PROFILE_PATH;
  return wrappedFetch(uri, parameters)
    .then((res) => res.ok)
    .catch(() => {
      return false;
    });
};

const fetchOrderBook = () => {
  const ApiUrl = RestEndPoints.GET_ORDER_BOOK;
  const { brokerClientId, brokerClientAccessToken } =
    getDynamicAppConfigs().brokerConfigs;
  const requestBody = `jData=${mandatoryRequestData(
    brokerClientId
  )}&jKey=${brokerClientAccessToken}`;

  const parameters = {
    method: 'POST',
    headers: requestHeaders,
    body: requestBody,
  };

  return wrappedFetch(ApiUrl, parameters)
    .then((response) => response.json())
    .then((result) => ({
      orderBookDetails: _.compact(
        _.orderBy(parseOrderDetails(result, 'order'), ['time'], ['desc'])
      ),
      headers: orderTableHeaders,
    }));
};

const fetchTradeBook = () => {
  const ApiUrl = RestEndPoints.GET_TRADE_BOOK;
  const { brokerClientId, brokerClientAccessToken } =
    getDynamicAppConfigs().brokerConfigs;
  const requestBody = `jData=${mandatoryRequestData(
    brokerClientId
  )}&jKey=${brokerClientAccessToken}`;

  const parameters = {
    method: 'POST',
    headers: requestHeaders,
    body: requestBody,
  };

  return wrappedFetch(ApiUrl, parameters)
    .then((response) => response.json())
    .then((result) => {
      return {
        tradeBookDetails: _.compact(
          _.orderBy(parseOrderDetails(result, 'trade'), ['time'], ['desc'])
        ),
        headers: tradeTableHeaders,
      };
    });
};

const fetchPositionBook = () => {
  const ApiUrl = RestEndPoints.UPDATE_POSITION_BOOK;
  const { brokerClientId, brokerClientAccessToken } =
    getDynamicAppConfigs().brokerConfigs;
  const requestBody = `jData=${mandatoryRequestData(
    brokerClientId
  )}&jKey=${brokerClientAccessToken}`;

  const parameters = {
    method: 'POST',
    headers: requestHeaders,
    body: requestBody,
  };

  return wrappedFetch(ApiUrl, parameters)
    .then((response) => response.json())
    .then((result) => {
      return {
        positionBookDetails: _.compact(
          _.orderBy(
            parseOrderDetails(result, 'position'),
            ['extraDetails.isOpenPosition'],
            ['desc']
          )
        ),
        headers: postitionTableHeaders,
      };
    });
};

const fetchHoldingBook = async () => {
  const ApiUrl = RestEndPoints.GET_HOLDINGS;
  const { brokerClientId, brokerClientAccessToken } =
    getDynamicAppConfigs().brokerConfigs;
  const requestData = {
    uid: brokerClientId,
    actid: brokerClientId,
    prd: 'C',
  };
  const requestBody = `jData=${JSON.stringify(
    requestData
  )}&jKey=${brokerClientAccessToken}`;

  const parameters = {
    method: 'POST',
    headers: requestHeaders,
    body: requestBody,
  };

  const holdings = await wrappedFetch(ApiUrl, parameters)
    .then((response) => response.json())
    .then((result) => result);

  const holdingBookSymbolDetails = _.join(
    _.map(holdings, (holding) => {
      return _.replace(_.get(holding, 'exch_tsym[0].tsym', ''), '-EQ', '');
    }),
    ','
  );

  const additionalDataDetails = await InstrumentDataAdaptor.getData(
    holdingBookSymbolDetails
  );

  const updatedHoldings = _.map(holdings, (holding) => {
    const symbol = _.replace(
      _.get(holding, 'exch_tsym[0].tsym', ''),
      '-EQ',
      ''
    );
    return {
      ...holding,
      firstOpen: _.get(additionalDataDetails?.marketData, symbol, {}).firstOpen,
    };
  });

  return {
    holdingBookDetails: _.compact(parseHoldingsBook(updatedHoldings)),
    headers: holdingTableHeaders,
  };
};

const placeOrder = async (placeOrderConfigs) => {
  const ApiUrl = RestEndPoints.PLACE_ORDER;
  const { brokerClientId, brokerClientAccessToken } =
    getDynamicAppConfigs().brokerConfigs;

  const formattedConfig = await Promise.all(
    _.map(placeOrderConfigs, (orderConfig) => {
      const orderDetails = parsePlaceOrder(orderConfig, brokerClientId);
      const requestBody = `jData=${JSON.stringify(
        orderDetails
      )}&jKey=${brokerClientAccessToken}`;

      const parameters = {
        method: 'POST',
        headers: requestHeaders,
        body: requestBody,
        isOrderPlacerApi: true,
      };

      return wrappedFetch(ApiUrl, parameters)
        .then((response) => response.json())
        .catch((e) => [e]);
    })
  );

  return _.chain(formattedConfig)
    .map(({ stat, norenordno }) => (stat === 'Ok' ? norenordno : null))
    .value();
};

const cancelPlaceOrder = ({ orderNo }) => {
  const ApiUrl = RestEndPoints.CANCEL_PLACE_ORDER;
  const { brokerClientId, brokerClientAccessToken } =
    getDynamicAppConfigs().brokerConfigs;
  const requestData = {
    uid: brokerClientId,
    actid: brokerClientId,
    norenordno: orderNo,
  };
  const requestbody = `jData=${JSON.stringify(
    requestData
  )}&jKey=${brokerClientAccessToken}`;

  const parameters = {
    method: 'POST',
    headers: requestHeaders,
    body: requestbody,
    redirect: 'follow',
  };

  return wrappedFetch(ApiUrl, parameters).then((response) => response.json());
};

const modifyPlaceOrder = (placeOrderConfigs) => {
  const ApiUrl = RestEndPoints.MODIFY_ORDER;

  const { brokerClientId, brokerClientAccessToken } =
    getDynamicAppConfigs().brokerConfigs;

  const orderDetails = parseModifyOrder(placeOrderConfigs?.[0], brokerClientId);
  const requestbody = `jData=${JSON.stringify(
    orderDetails
  )}&jKey=${brokerClientAccessToken}`;

  const parameters = {
    method: 'POST',
    headers: requestHeaders,
    body: requestbody,
    redirect: 'follow',
  };

  return wrappedFetch(ApiUrl, parameters)
    .then((response) => response.json())
    .then((data) => [data?.result])
    .catch((e) => [e]);
};

const fetchMarginCalculator = async (orderConfigs) => {
  const ApiUrl = RestEndPoints.MARGIN_CALCULATION;
  const availableMarginUrl = RestEndPoints.AVAILABLE_MARGIN;

  const { brokerClientId, brokerClientAccessToken } =
    getDynamicAppConfigs().brokerConfigs;

  const orderDetails = await Promise.all(
    _.map(orderConfigs, (config) => parseMarginCalculator(config, brokerClientId)));

  const availableMargin = await wrappedFetch(availableMarginUrl, {
    method: 'POST',
    headers: requestHeaders,
    body: `jData=${JSON.stringify(
      orderDetails[0]
    )}&jKey=${brokerClientAccessToken}`
  })
    .then((response) => response.json())
    .then((data) => data)
    .catch((e) => [e.message]);

  const formattedResponse = await Promise.all(_.map(orderDetails, (orderDetail) => {
    const requestBody = `jData=${JSON.stringify(
      orderDetail
    )}&jKey=${brokerClientAccessToken}`;

    const parameters = {
      method: 'POST',
      headers: requestHeaders,
      body: requestBody,
    };

    return wrappedFetch(ApiUrl, parameters)
      .then((response) => response.json())
      .then((data) => data)
      .catch((e) => [e.message]);
  }));

  const totalMarginRequired =
    _.chain(formattedResponse)
      .map(({ marginusedtrade }) => parseFloat(marginusedtrade))
      .sum()
      .value() || 0;

  const totalMarginAvailable = _.get(availableMargin, 'cash', 0);

  return { totalMarginRequired, totalMarginAvailable };
};

export {
  fetchOrderBook,
  fetchPositionBook,
  fetchTradeBook,
  fetchHoldingBook,
  placeOrder,
  isValidAccessToken,
  fetchAccessToken,
  cancelPlaceOrder,
  sendOtp,
  modifyPlaceOrder,
  fetchMarginCalculator,
};
